import React, { useEffect, useState } from "react";

// Reusable Header Component
const PanelHeader = ({ title, isCollapsed, onClick }) => (
  <div
    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 rounded-t-lg"
    onClick={onClick}
  >
    <h3 className="font-bold text-gray-800 text-lg leading-none">{title}</h3>
    <button className="flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors">
      {isCollapsed ? (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      ) : (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 15l7-7 7 7"
          />
        </svg>
      )}
    </button>
  </div>
);

const BottomPanels = ({ normalized, collapsePanels }) => {
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(false);
  const [isTokenDetailsCollapsed, setIsTokenDetailsCollapsed] =
    useState(false);

  // Handle panel toggle - both panels should open/close together
  const togglePanels = () => {
    const newState = !isLegendCollapsed;
    setIsLegendCollapsed(newState);
    setIsTokenDetailsCollapsed(newState);
  };

  // Collapse from parent signal (e.g., clicking canvas)
  useEffect(() => {
    if (collapsePanels) {
      setIsLegendCollapsed(true);
      setIsTokenDetailsCollapsed(true);
    }
  }, [collapsePanels]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white border-t border-gray-200">
      {/* Legend Panel */}
      <div
        className={`flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-300 ${
          isLegendCollapsed ? "max-h-12" : "max-h-64"
        }`}
      >
        <PanelHeader
          title={`${normalized?.provider || "Token"} Blast Radius`}
          isCollapsed={isLegendCollapsed}
          onClick={togglePanels}
        />

        {!isLegendCollapsed && (
          <div className="px-4 pb-4 overflow-y-auto flex-1">
            <div className="space-y-2 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                <span>
                  {normalized?.provider
                    ? `${normalized.provider} Token`
                    : "Token"}
                </span>
              </div>
              
              {/* Show different legend based on provider */}
              {(normalized?.provider || "").toLowerCase() === "sendgrid" ? (
                <>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-emerald-500 rounded-full mr-2"></div>
                    <span>Messaging Core</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-600 rounded-full mr-2"></div>
                    <span>Security & Access</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                    <span>Analytics</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-violet-500 rounded-full mr-2"></div>
                    <span>Templates & Content</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-amber-500 rounded-full mr-2"></div>
                    <span>Integrations</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-cyan-500 rounded-full mr-2"></div>
                    <span>User Management</span>
                  </div>
                </>
              ) : (normalized?.provider || "").toLowerCase() === "huggingface" ? (
                <>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                    <span>Read Access</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                    <span>Write Access</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-purple-500 rounded-full mr-2"></div>
                    <span>Private Models</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                    <span>
                      Repositories (
                      {normalized?.total_repositories ||
                        normalized?.repositories?.length ||
                        0}
                      )
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-purple-500 rounded-full mr-2"></div>
                    <span>
                      Permissions (
                      {normalized?.total_scopes || normalized?.scopes?.length || 0}
                      )
                    </span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-cyan-500 rounded-full mr-2"></div>
                    <span>Repository Categories</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
                    <span>Permission Categories</span>
                  </div>
                </>
              )}
            </div>
            <div className="mt-3 text-xs text-gray-600">
              <p>
                Click nodes with{" "}
                <span className="inline-flex items-center justify-center w-4 h-4 bg-green-500 text-white rounded-full text-xs font-bold mx-1">
                  +
                </span>{" "}
                to expand
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Token Details Panel */}
      <div
        className={`flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 transition-all duration-300 ${
          isTokenDetailsCollapsed ? "max-h-12" : "max-h-64"
        }`}
      >
        <PanelHeader
          title="Token Details"
          isCollapsed={isTokenDetailsCollapsed}
          onClick={togglePanels}
        />

        {!isTokenDetailsCollapsed && (
          <div className="px-4 pb-4 overflow-y-auto flex-1">
            <div className="space-y-1 text-sm text-gray-600">
              <p>
                <strong>Provider:</strong> {normalized?.provider || "Unknown"}
              </p>
              <p>
                <strong>User:</strong> {normalized?.token_user || "Unknown"}
              </p>
              {(normalized?.provider || "").toLowerCase() === "sendgrid" ? (
                <>
                  <p>
                    <strong>Key Type:</strong> {normalized?.key_type || "Unknown"}
                  </p>
                  <p>
                    <strong>Valid Key:</strong> {normalized?.valid_key ? "Yes" : "No"}
                  </p>
                  <p>
                    <strong>2FA Required:</strong> {normalized?.scopes?.some(scope => scope['2fa_required']) ? "Yes" : "No"}
                  </p>
                  <p>
                    <strong>Total Scopes:</strong> {normalized?.total_scopes || normalized?.scopes?.length || 0}
                  </p>
                  <p>
                    <strong>Raw Scopes:</strong> {normalized?.total_raw_scopes || 0}
                  </p>
                </>
              ) : (normalized?.provider || "").toLowerCase() === "huggingface" ? (
                <>
                  <p>
                    <strong>Token Type:</strong> {normalized?.token_type || "Unknown"}
                  </p>
                  <p>
                    <strong>Valid Token:</strong> {normalized?.valid_token ? "Yes" : "No"}
                  </p>
                  <p>
                    <strong>Total Models:</strong> {normalized?.total_models || 0}
                  </p>
                  <p>
                    <strong>Organizations:</strong> {normalized?.total_organizations || 0}
                  </p>
                </>
              ) : (normalized?.provider || "").toLowerCase() !== "slack" && (
                <>
                  <p>
                    <strong>Type:</strong> {normalized?.token_type || "Unknown"}
                  </p>
                  <p>
                    <strong>Expires:</strong>{" "}
                    {normalized?.token_expiration
                      ? new Date(
                          normalized.token_expiration
                        ).toLocaleDateString()
                      : "Unknown"}
                  </p>
                  <p>
                    <strong>Days Left:</strong>{" "}
                    {normalized?.days_remaining || 0}
                  </p>
                  <p>
                    <strong>Repositories:</strong>{" "}
                    {normalized?.total_repositories ||
                      normalized?.repositories?.length ||
                      0}
                  </p>
                </>
              )}
              <p>
                <strong>Permissions:</strong>{" "}
                {normalized?.total_scopes || normalized?.scopes?.length || 0}
              </p>

              {(normalized?.provider || "").toLowerCase() === "slack" && (
                <div className="mt-2 space-y-1">
                  {normalized?.slack_team_name && (
                    <p>
                      <strong>Slack Team:</strong> {normalized.slack_team_name}
                    </p>
                  )}
                  {normalized?.slack_team_id && (
                    <p>
                      <strong>Slack Team ID:</strong> {normalized.slack_team_id}
                    </p>
                  )}
                  {normalized?.slack_user_name && (
                    <p>
                      <strong>Slack User:</strong> {normalized.slack_user_name}
                    </p>
                  )}
                  {normalized?.slack_user_id && (
                    <p>
                      <strong>Slack User ID:</strong> {normalized.slack_user_id}
                    </p>
                  )}
                  {normalized?.slack_url && (
                    <p>
                      <strong>Slack URL:</strong>{" "}
                      <a
                        href={normalized.slack_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {normalized.slack_url}
                      </a>
                    </p>
                  )}
                </div>
              )}

              {normalized?.total_models != null && (
                <p>
                  <strong>Models:</strong> {normalized?.total_models}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BottomPanels;
